// Ranní přehled a hlídač lhůt — signály (F4 4.10; docs/11 §7 bod 1; kontrakt tagy Archiv a Fronty):
//   GET  /ja/prehled?datum                 RanniPrehled — režim podle role, dlaždice s počty, dnešní agenda, otevřené
//                                          signály (≤ 10 nejzávažnějších + `signaly_celkem`), fakta pro shrnutí a
//                                          `shrnuti` (jen se zapnutou AI; jinak `shrnuti_duvod`); `datum` jen pro testy
//   POST /ja/prehled/shrnuti               asistent složí 2–4 věty z fakt → { shrnuti?, shrnuti_duvod?, shrnuti_kdy?, fakta }
//                                          (kód zkontroluje čísla; bez AI 200 s důvodem; nic neukládá kromě mezipaměti dne)
//   GET  /signaly?stav&druh&zavaznost&entita&entita_id&jen_moje&hledat&strana&velikost
//                                          SignalSeznam + `otevrene` (bez ohledu na filtr) + `podle_zavaznosti`
//   GET  /signaly/{id}                     detail signálu
//   POST /signaly/{id}/vyresit { poznamka? } → Signal (409 `signal.uz_vyreseny` podruhé — proto bez Undo)
//   POST /signaly/prepocitat               admin / jednatel: spustit pravidla hlídače teď → počty (idempotentní)
// Všechna čísla a texty spočítal server (řidič nedostane ceny ani cizí jízdy — 08 §4); UI z toho jen kreslí.
import type { paths, Schema } from '@ecoser/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';

export type RanniPrehled = Schema<'RanniPrehled'>;
export type PrehledDlazdice = Schema<'PrehledDlazdice'>;
export type PrehledAgenda = Schema<'PrehledAgenda'>;
export type Signal = Schema<'Signal'>;
export type SignalSeznam = Schema<'SignalSeznam'>;
export type ZavaznostSignalu = Signal['zavaznost'];
export type DruhSignalu = Signal['druh'];
export type StavSignalu = Signal['stav'];
export type ShrnutiOdpoved =
  paths['/ja/prehled/shrnuti']['post']['responses']['200']['content']['application/json'];
export type PrepocetSignalu =
  paths['/signaly/prepocitat']['post']['responses']['200']['content']['application/json'];

/** Klíč akce pro „Přepočítat teď“ (kontrakt `x-opravneni: signal.prepocitat`; smí admin a jednatel). */
export const AKCE_PREPOCITAT_SIGNALY = 'signal.prepocitat';

/** Nejvýš tolik signálů ukazuje Domů (kontrakt: `signaly` = ≤ 10 nejzávažnějších). */
export const SIGNALU_NA_DOMU = 10;

// --- Ranní přehled ---------------------------------------------------------------------------------------

export const KLIC_PREHLED = ['ja', 'prehled'] as const;
export const klicPrehledu = (datum?: string) => [...KLIC_PREHLED, datum ?? 'dnes'] as const;

/** Přehled se obnovuje po 5 minutách (počty dlaždic a signály se přes den mění). */
export const INTERVAL_OBNOVY_PREHLEDU_MS = 5 * 60_000;

export function nacistPrehled(datum?: string, signal?: AbortSignal): Promise<RanniPrehled> {
  const q = datum ? `?datum=${encodeURIComponent(datum)}` : '';
  return api.get<RanniPrehled>(`/ja/prehled${q}`, { signal });
}

/** Ranní přehled přihlášeného pro Domů; `datum` jen pro testy (výchozí dnes v Praze na serveru). */
export function useRanniPrehled(datum?: string, povoleno = true) {
  return useQuery({
    queryKey: klicPrehledu(datum),
    queryFn: ({ signal }) => nacistPrehled(datum, signal),
    enabled: povoleno,
    refetchInterval: INTERVAL_OBNOVY_PREHLEDU_MS,
    placeholderData: (predchozi) => predchozi,
    // Chyba se ukáže rovnou s „Zkusit znovu“ (jako stav serveru); server bez endpointu = 404 hned.
    retry: false,
  });
}

/** Sestavit souhrn dne (AI) z fakt přehledu — nic se neukládá, jen mezipaměť na den. */
export const sestavitShrnuti = () => api.post<ShrnutiOdpoved>('/ja/prehled/shrnuti', {});

/** Po zápisu, který mění počty nebo signály (vyřešení, přepočet, zakázka…), se přehled načte znovu. */
export function obnovitPrehled(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: KLIC_PREHLED });
  void queryClient.invalidateQueries({ queryKey: KLIC_SIGNALY });
}

// --- Signály ------------------------------------------------------------------------------------------------

export const KLIC_SIGNALY = ['signaly'] as const;

export interface FiltrSignalu {
  stav?: StavSignalu;
  druh?: string;
  zavaznost?: ZavaznostSignalu;
  entita?: string;
  entita_id?: string;
  jen_moje?: boolean;
  hledat?: string;
  strana?: number;
  velikost?: number;
}

/** Výchozí velikost stránky v draweru Všechny signály. */
export const VELIKOST_STRANY_SIGNALU = 20;

export function dotazSignalu(filtr: FiltrSignalu): string {
  const q = new URLSearchParams();
  if (filtr.stav) q.set('stav', filtr.stav);
  if (filtr.druh) q.set('druh', filtr.druh);
  if (filtr.zavaznost) q.set('zavaznost', filtr.zavaznost);
  if (filtr.entita) q.set('entita', filtr.entita);
  if (filtr.entita_id) q.set('entita_id', filtr.entita_id);
  if (filtr.jen_moje) q.set('jen_moje', 'true');
  if (filtr.hledat?.trim()) q.set('hledat', filtr.hledat.trim());
  q.set('strana', String(filtr.strana ?? 1));
  q.set('velikost', String(filtr.velikost ?? VELIKOST_STRANY_SIGNALU));
  return `?${q.toString()}`;
}

export function nacistSignaly(filtr: FiltrSignalu, signal?: AbortSignal): Promise<SignalSeznam> {
  return api.get<SignalSeznam>(`/signaly${dotazSignalu(filtr)}`, { signal });
}

/** Seznam signálů podle filtru (drawer Všechny signály); řazení závažnost → termín dělá server. */
export function useSignaly(filtr: FiltrSignalu, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_SIGNALY, 'seznam', dotazSignalu(filtr)] as const,
    queryFn: ({ signal }) => nacistSignaly(filtr, signal),
    enabled: povoleno,
    placeholderData: (predchozi) => predchozi,
    retry: false,
  });
}

export const nacistSignal = (id: string) => api.get<Signal>(`/signaly/${encodeURIComponent(id)}`);

/**
 * „Vyřešeno“ s nepovinnou poznámkou. Bez Undo: server vrátí podruhé 409 `signal.uz_vyreseny`;
 * když příčina trvá, hlídač signál po 7 dnech založí znovu.
 */
export const vyresitSignal = (id: string, poznamka?: string) =>
  api.post<Signal>(
    `/signaly/${encodeURIComponent(id)}/vyresit`,
    poznamka?.trim() ? { poznamka: poznamka.trim() } : {},
  );

/** Spustit pravidla hlídače hned (admin, jednatel) — vrací počty založených / potvrzených / vyřešených. */
export const prepocitatSignaly = () => api.post<PrepocetSignalu>('/signaly/prepocitat', {});

/** Hláška po přepočtu: „Přepočítáno: založeno 2, potvrzeno 5, vyřešeno automaticky 1.“ */
export function popisPrepoctu(p: PrepocetSignalu): string {
  const preskocena = (p.pravidla ?? []).filter((r) => r.preskoceno).length;
  const zaklad = `Přepočítáno: založeno ${p.zalozeno}, potvrzeno ${p.potvrzeno}, vyřešeno automaticky ${p.vyreseno_automaticky}.`;
  return preskocena > 0 ? `${zaklad} Přeskočená pravidla (chybí data): ${preskocena}.` : zaklad;
}

// --- Popisky ------------------------------------------------------------------------------------------------

/** Popisek závažnosti pro čtečku a filtr (tečka je jen barva). */
export const ZAVAZNOSTI_SIGNALU: Readonly<Record<ZavaznostSignalu, string>> = {
  info: 'Informace',
  upozorneni: 'Upozornění',
  kriticke: 'Kritické',
};

/** Pořadí závažností od nejvážnější (pro řazení, když server pošle víc než ukazujeme). */
export const PORADI_ZAVAZNOSTI: Readonly<Record<ZavaznostSignalu, number>> = {
  kriticke: 0,
  upozorneni: 1,
  info: 2,
};

/** Popisky druhů signálů jako záloha, když server `druh_popis` nepošle (kontrakt `Signal.druh`). */
export const DRUHY_SIGNALU: Readonly<Record<DruhSignalu, string>> = {
  smlouva_koncovka_lhuta: 'Blíží se lhůta smlouvy s koncovkou',
  audit_naprava_po_terminu: 'Náprava z auditu po termínu',
  zakazka_bez_prideleni: 'Zakázka bez přidělení řidiče',
  zakazka_po_terminu: 'Zakázka po termínu bez zahájení',
  zakazka_doplnit_mnozstvi: 'Zakázka čeká na doplnění množství',
  zakazka_ke_kontrole_stara: 'Zakázka dlouho ke kontrole',
  odlozene_po_terminu: 'Odložené vyúčtování po termínu',
  svoz_ceka_dlouho: 'Požadavek na svoz čeká dlouho',
  svoz_specha: 'Požadavek na svoz spěchá',
  iso_udalost_po_terminu: 'Událost ISŘ po termínu',
  kontrola_vozidla_chybi: 'Chybí ranní kontrola vozidla',
  admin_review_stary: 'Položka ke schválení čeká dlouho',
  ukol_po_terminu: 'Úkol po termínu',
  zarizeni_povoleni_konci: 'Končí povolení zařízení',
  spolecna_jizda_nespojena: 'Zakázky ke spojení do společné jízdy',
  vozidlo_lhuta: 'Lhůta vozidla (STK, tachograf…)',
  gps_vypadek: 'Napojení GPS ONI vypadlo (úloha nejde déle než 30 minut)',
  gps_prijezd_rozpor: 'Příjezd podle GPS se liší od tlačítka řidiče (nad 15 min)',
  gps_neplanovana_zastavka: 'Neplánovaná zastávka vozidla mimo známá místa',
  gps_zakazka_bez_navstevy: 'Hotová zakázka bez příjezdu podle GPS',
  gps_kontejner_jinde: 'Kontejner s GPS jednotkou jinde než v evidenci',
  gps_prestavka: 'Řidič jel bez přestávky přes limit',
  gps_jizda_mimo_dochazku: 'Jízda mimo docházku řidiče',
  gps_jizda_mimo_dobu: 'Jízda mimo provozní dobu nebo o víkendu',
  gps_rychlost: 'Překročení rychlosti',
  gps_vyboceni: 'Vybočení z trasy (km nad plán)',
  vazni_listek_neovereny: 'Neověřený vážní lístek ke kontrole',
  splatka_po_terminu: 'Splátka splátkového kalendáře po termínu',
  pohledavka_po_2_upomince: 'Pohledávka po 2. upomínce bez úhrady',
  vaha_se_nehlasi: 'Vážní služba u váhy se dlouho neozvala',
  smlouva_bez_podpisu: 'Smlouva čeká na podpis',
  smlouva_doba_urcita_konci: 'Smlouva na dobu určitou končí',
  pokladna_hotovost: 'Hotovost v pokladně k dořešení',
  zakazka_bez_souradnic: 'Zakázka dne bez souřadnic místa',
  gps_stani_motor_den: 'Vozidlo stálo s běžícím motorem',
  zakazka_ceka_na_zakaznika: 'Zakázka čeká na zákazníka',
  ecosmlouva_pausaly_nevyuctovane: 'Nevyúčtované paušály ECOsmlouvy',
  baliky_papiru_k_odvozu: 'Balíky papíru čekají na odvoz do papírny',
  evidence_radky_s_chybou: 'Řádky průběžné evidence s nedostatkem (partner, tok, kovy)',
  ispop_kampan_termin: 'Roční hlášení ISPOP: termín 28. 2.',
  ispop_doplnene_hlaseni:
    'Rok se po odeslání hlášení znovu otevřel — úřadu je potřeba poslat doplněné hlášení',
  podklady_ke_kontrole:
    'Podklady od zákazníků čekají na kontrolu a převod do evidence (staré podklady a podklady za uzavřený měsíc)',
  karta_odpadu_chybi_udaje: 'Karta nebezpečného odpadu bez protokolu o rozboru',
  pio_ceka_na_podpis: 'PIO / ZPO čeká na podpis zákazníka',
  olpno_po_terminu: 'Ohlašovací list OLPNO po lhůtě',
  olpno_neuzavreny: 'Neuzavřený denní ohlašovací list',
  olpno_ceka_na_odeslani: 'SEPNO: požadavky čekají ve frontě',
  celni_mesicni_termin: 'Celní správa: měsíční oznámení ZMO nepodané',
  // W6 díl 2 (migrace 1450) — oznámení o přijetí ZMO podle § 134zf.
  celni_oznameni_134zf_termin:
    'Oznámení o přijetí ZMO (§ 134zf) není podané — správci daně nejméně 24 hodin před přijetím',
  celni_prijem_bez_oznameni_134zf:
    'Naplánovaný svoz odpadního oleje k nám nemá oznámení podle § 134zf — podat nejméně 24 hodin před přijetím',
  // W6 díl 4 (migrace 1645) — datová zpráva bez potvrzeného doručení.
  isds_zprava_nedorucena:
    'Datová zpráva odeslaná z aplikace nemá po 24 hodinách potvrzené doručení — obnovit stav, nebo poslat jinak',
  evidence_kontroly_roku: 'Kontroly evidence nad rokem hlásí chyby',
  oznameni_obci_termin: 'Oznámení obcím do 15. 1. nejsou odeslaná',
  eko_kom_vykaz_termin: 'Výkaz EKO‑KOM za minulé čtvrtletí není odeslaný',
  hrzo_zmena: 'Změna v Registru zařízení u sledovaného subjektu',
  hrzo_konec_povoleni: 'Sledovanému zařízení končí povolení',
  vlastni_termin: 'Blíží se vlastní termín (revize, cejchování, školení…)',
  kapacita_zarizeni: 'Kapacita zařízení se blíží vyčerpání nebo je překročená',
  doba_skladovani: 'Odpad je na skladě déle, než povoluje provozní řád',
  cena_paliva_chybi: 'Chybí cena nafty za minulý měsíc (palivový příplatek)',
  kurz_cnb_chybi: 'Chybí kurz ČNB k přijaté faktuře v cizí měně',
  banka_nenactena: 'Banka se dnes nenačetla',
  banka_souhlas_konci: 'Souhlas s bankou brzy skončí — obnovit v Nastavení › Firma',
  banka_souhlas_vyprsel: 'Souhlas s bankou vypršel — pohyby se nenačítají',
  zadost_ceka_dlouho: 'Žádost o volno čeká na schválení',
  zam_lhuta_konci: 'Končí nebo propadla lhůta zaměstnance',
  zam_oopp_obnova: 'Ochranný pracovní prostředek k obnově',
  mzdy_sazby_kontrola: 'Zkontrolujte sazby odvodů a hranice dohod pro nový rok',
  // Dovolená se do dalšího roku nepřevádí (17. 10. 2026) — `dovolena_prevod` už nevzniká, popisek jen pro historii.
  dovolena_prevod: 'Dovolená převedena do nového roku (zrušeno)',
  dovolena_precerpana: 'Přečerpaná dovolená za minulý rok',
  dovolena_nevycerpana: 'Nevyčerpaná dovolená — vybrat do konce roku, jinak se proplatí',
  upominky_profit_rucne: 'Faktury čekají na ruční upomínku (z PROFITu, zákazníci bez e‑mailu)',
  // Krok 12.1b (migrace 0330) — schválení a úhrada přijatých faktur přes banku.
  prijata_faktura_ke_schvaleni: 'Přijatá faktura čeká na vaše schválení',
  banka_davka_k_potvrzeni: 'Návrh platební dávky čeká na potvrzení',
  banka_davka_ceka_na_podpis: 'Platební dávka čeká na podpis v bance',
  banka_platba_neznamy_vysledek: 'Platba v bance s neznámým výsledkem — dohledat',
  banka_platba_nedostatek: 'Na účtu není dost peněz na platby',
  banka_platba_zamitnuta: 'Banka platbu zamítla',
  banka_platba_zadrzena: 'Platba zadržená nebo nedorazila na výpis',
  // Krok 11.4 (migrace 0423) — události ISŘ a nápravná opatření.
  iso_udalost_nova: 'Nová událost ISŘ čeká na převzetí k řešení',
  napravne_opatreni_po_terminu: 'Nápravné opatření po termínu',
  // Krok 11.1 (migrace 0440) — řízená dokumentace ISŘ.
  iso_dokument_nepotvrzen: 'Předpis ISŘ po lhůtě — příjemci ho ještě nepotvrdili',
  // Krok 11.6 (migrace 0445) — zařízení ISŘ, revize a údržba.
  iso_revize_zarizeni: 'Revize nebo kontrola zařízení ISŘ po termínu nebo se blíží',
  iso_udrzba_stroje: 'Údržba stroje po termínu, do 7 dní nebo zatím neprovedená',
  // Krok 11.7 (migrace 0463) — provozní deník, ČOV a zkoušky na alkohol (bez jmen a výsledků).
  provozni_denik_chybi: 'Chybí zápis provozního deníku (dnes po připomínce nebo za pracovní den)',
  cov_rozbor_termin: 'Rozbor vody z ČOV: blíží se odběr, je po termínu, nebo čeká na výsledky',
  cov_rozbor_nad_limit: 'Poslední rozbor vody z ČOV je nad limitem',
  alkohol_zkouska_termin: 'Je čas na zkoušku na alkohol (uplynul interval)',
  cov_doklad_platnost: 'Doklad ČOV (akreditace, osvědčení) končí nebo propadl',
  audit_po_terminu: 'Audit u zákazníka měl proběhnout a nebyl zahájen',
  audit_ke_kontrole_dlouho: 'Audit čeká dlouho na kontrolu vedoucím',
  // Krok 11.3 (migrace 0471) — plán školení, propadlé školení ISŘ, e‑kurz nesplněný do termínu.
  iso_skoleni_plan_blizi: 'Naplánované školení se blíží',
  iso_skoleni_plan_nepotvrzeno: 'Školení mělo proběhnout — účast není zapsaná',
  iso_skoleni_propadlo: 'Povinné školení ISŘ propadlo nebo končí',
  iso_kurz_nesplneno: 'E‑kurz nesplněný do termínu',
  prezkoumani_isr_chybi: 'Přezkoumání ISŘ za uplynulý rok není schválené',
  audit_opora_overit:
    'Právní opora šablony auditu k ověření (nové znění předpisu), ke schválení nebo nepřipravená',
  gdpr_anonymizace_ceka:
    'Osobní údaje po lhůtě čekají na smazání (náhled — občané po 5 letech, obsah offline fronty a logu asistenta po 90 dnech) nebo mazání selhalo',
  // Krok 6.18 (migrace 0633) — služby na zakázce.
  marna_jizda_k_potvrzeni: 'Řidič nahlásil marnou jízdu — potvrďte ji, nebo zamítněte',
  sluzba_bez_ceny: 'Služba na zakázce nemá cenu — faktura bez ní nepůjde vystavit',
  // Krok 5.6b (migrace 0723) — vozidlo v areálu bez odjezdového vážení.
  vazni_listek_otevreny: 'Vozidlo je v areálu déle než limit — otevřený vážní lístek bez odjezdu',
  // Krok 6.15 (migrace 0721) — pokladna skladu a platební terminál.
  prijem_kartou_bez_transakce:
    'Platba kartou u pokladny skladu je déle než den bez čísla transakce terminálu',
  doklady_pokladny_neodeslane:
    'Doklady z pokladny skladu neodešly zákazníkovi e‑mailem a nebyly ani vytištěné',
  terminal_neodpovedel:
    'Platba kartou u pokladny: terminál neodpověděl, nebo zákazník zaplatil a pokladna se nedokončila',
  financni_urady_kontrola: 'Zkontrolujte účty finančních úřadů pro nový rok',
  dodavatel_dph_zhorseni:
    'Dodavatel s nezaplacenou fakturou: registr plátců DPH hlásí zhoršení (nespolehlivý plátce, účet)',
  // Rozhodnutí 28. 9. 2026 k 05/1072 (migrace 1401).
  adr_doklad_neaktualni:
    'Přepravní doklad ADR je po změně ohlašovacího listu neaktuální — vystavte novou verzi',
  // Krok 12.7 díl 2 (migrace 1441).
  dovoz_rada_mezera:
    'V papírové řadě smluv, ECOsmluv nebo dodacích listů roku 2026 chybí čísla — dovezte je, nebo označte „Nevzniklo“',
  // Rozhodnutí 27. 9. 2026 k 05/390 (migrace 1550).
  ilno_neaktualni:
    'Vydané ILNO neodpovídají platné dohodě ADR — srovnejte je v ILNO › Neaktuální ILNO',
  // Rozhodnutí 27. 9. 2026 k 05/291 (migrace 1570); 29. 9. 2026 k 05/1120 i odečty proti řadě (bez migrace).
  vozidlo_tachometr_nesrovnalost:
    'Nesrovnalost tachometru — ranní kontrola má nižší stav km než den předtím, nebo odečet (ze servisu, dokladu, ruční či z ranní kontroly) nesedí do řady stavů',
  // Rozhodnutí 27. 9. 2026 k 05/136 (migrace 1580).
  neodeslane_zapisy:
    'V tabletu zůstaly po konci směny neodeslané zápisy — tablet je odešle, až bude mít signál',
  // Rozhodnutí 27. 9. 2026 k 05/128 (migrace 1591).
  zaloha_trvale_neuspesna:
    'Záloha dokumentu na OneDrive/SharePoint se nepovedla ani za 7 dní — odstraňte příčinu a dosynchronizujte',
  // Rozhodnutí 24. 9. 2026 k 05/97 (migrace 1670).
  odchod_s_nesplnenym_ukolem:
    'Směna ukončená s nesplněným prioritním úkolem („Přesto ukončit“ s důvodem) — nadřízenému',
  // Rozhodnutí 28. 9. 2026 k 05/1074 (migrace 1761).
  adr_doklad_chybi:
    'Jízda s nebezpečným odpadem nemá přepravní doklad ADR — vystavte ho před odjezdem',
  // Předání řidič → sklad (migrace 1781, 28. 9. 2026).
  zakazka_nepredana_skladu:
    'Jízda řidiče na sklad je podepsaná, ale řidič ji skladu nepředal — sklad ji nemůže zvážit ani doplnit množství',
  // Kroky 6.17, 6.6c a 12.3 (migrace 0641, 0690, 0691) — v kontraktu `Signal.druh` chyběly, doplněno 27. 9. 2026.
  zapocet_ke_schvaleni: 'Návrh vzájemného zápočtu čeká na schválení (admin, jednatel)',
  zapocet_k_provedeni:
    'Schválený zápočet čeká na podpis dohody obou stran nebo odeslání oznámení — doklady jsou zarezervované',
  banka_karta_bez_dokladu:
    'Nákup firemní kartou nemá doklad — přiřaďte fakturu, nebo označte „bez faktury“',
  ai_naklady_neobvykle:
    'Neobvyklá spotřeba AI — poslední týden výrazně nad průměrem předchozích týdnů',
  // Krok 6.12 (migrace 0906) — řada faktur z PROFITu.
  profit_rada_mezera: 'V řadě faktur z PROFITu chybí čísla',
  // Krok 12.2 (migrace 0901) — schránka faktur, sledovaná složka a fronta čtení přijatých faktur.
  prijata_faktura_navrh_ceka:
    'Přijaté faktury čekají na potvrzení (blíží se splatnost) nebo e‑mail faktur čeká na rozhodnutí',
  schranka_faktur_chyba: 'Schránku faktur nebo sledovanou složku se nedaří číst',
  // Rozhodnutí zadavatele 27. 9. 2026, 05/276 (migrace 1644).
  mzdy_podklad_chybi: 'Za minulý měsíc chybí podklad pro mzdovou účetní',
  // Rozhodnutí zadavatele 30. 9. 2026 (migrace 1920) — dobropis z PROFITu bez potvrzené původní faktury.
  profit_dobropis_bez_faktury:
    'Dobropis z PROFITu čeká na potvrzení faktury, kterou opravuje — do té doby nesnižuje, kolik zbývá zaplatit',
  // Krok 12.3 (migrace 2050, rozhodnutí 2. 10. 2026) — kontroly souladu mezi registry.
  kontrola_souladu:
    'Nesoulad mezi registry (zákazník, provozovna, smlouva, ceník, zařízení, partner) — kontrola souladu na pozadí',
  // Rozhodnutí 3. 10. 2026 k 05/1626 (migrace 2101) — napojení na API e‑shopu NODY.
  eshop_nody_api_chyba:
    'Napojení na API e‑shopu NODY hodinu nefunguje — objednávky chodí jen e‑mailem',
  // Rozhodnutí zadavatele 3. 10. 2026 k 05/1572 b (migrace 2120) — jen admin a ti, kdo pracují za klienty.
  agenda_klienta:
    'Klient agendy: chyby v evidenci, roční hlášení, podklady z portálu nebo zmocnění v CRŽP čekají na vyřízení',
  // W22 (migrace 2160, 05/1662) — tankování z palivových karet MOL.
  tankovani_kontrola:
    'Tankování z palivové karty MOL k prověření — karta bez vozidla, víc litrů než nádrž, neobvyklá doba, daleko od vozidla nebo nesoulad s fakturou',
  tankovani_data_chybi:
    'Z MOL dlouho nepřišla žádná tankování (zrušeno 31. 10. 2026 — nahrazeno měsíční připomínkou výpisu z MOL)',
  gps_jednotka_nekomunikuje: 'Jednotka GPS nehlásí polohu',
  gps_ubytek_paliva: 'Úbytek paliva mimo tankování (orientačně)',
  obal_domluveno_do: 'Kontejner po domluveném termínu',
  // Migrace 2331 (3. 10. 2026, 05/1707) — platba na terminálu bez zakázky po konci dne nespárovaná.
  terminal_platba_nesparovana:
    'Zaplacená platba na terminálu bez zakázky není spárovaná se zakázkou — spárovat v Platbách na terminálu',
  // Migrace 3171 (6. 10. 2026, 05/129) — připomínka k vzoru dokumentu čeká na vyřízení déle než 7 dní.
  vzor_pripominka_nova:
    'Připomínka k vzoru dokumentu čeká na vyřízení déle než týden — posoudit ji a vzor opravit',
  // Migrace 3201 (6. 10. 2026, W35) — někdo má na rok tuzemské diety a rok nemá sazby stravného.
  stravne_sazby_chybi:
    'Chybí sazby stravného pro rok s tuzemskými dietami — zapsat je v Nastavení › Firma',
  // Migrace 3412 (7. 10. 2026) — obchodník za den otevřel neobvykle mnoho obchodních dat.
  cteni_neobvykle:
    'Obchodník dnes otevřel neobvykle mnoho zákazníků nebo dokumentů — podívat se v jeho kartě, co si prohlížel',
  // Migrace 3540 (8. 10. 2026, 05/2504 (b)) — změna na kartě zákazníka čeká na dodatek déle než 14 dní.
  zmena_ceka_na_dodatek:
    'Změna na kartě zákazníka čeká na dodatek déle než 14 dní — připravit dodatek ve Smlouvách › Čeká na dodatek',
  // Migrace 3601 (9. 10. 2026, W37, AUD-F16) — schválený audit čeká na uzavření a předání k fakturaci.
  audit_ceka_na_odeslani:
    'Schválený audit čeká na předání k fakturaci — otevřít audit a „Uzavřít audit a předat k fakturaci“',
  // Migrace 3661 (9. 10. 2026, W40, 05/2661) — zakázka prodeje čeká na hmotnost od odběratele.
  ceka_na_hmotnost_odberatele:
    'Prodej odběrateli čeká na jeho hmotnost — zapsat hmotnost od odběratele, nebo potvrdit „Bez vážení odběratele“',
  // Migrace 3691 (8. 10. 2026, W41) — požadavek na svoz, který máte vyřešit, čeká od předchozího pracovního dne.
  svoz_k_vyrizeni:
    'Máte vyřešit požadavek na svoz — vytvořit zakázku, zamítnout, nebo předat jinému',
  // Migrace 3951 (10. 10. 2026, rytmus svozů) — zákazník s pravidelným rytmem je po termínu dalšího svozu.
  zakaznik_po_terminu_svozu:
    'Zákazník je po termínu svozu — založit požadavek na svoz nebo zakázku, nebo svoz odložit s důvodem',
  // Migrace 3962 (10. 10. 2026) — zákazník s pravidelným odběrem je dlouho bez faktury.
  zakaznik_prestal_odebirat:
    'Zákazník s pravidelným odběrem je bez faktury aspoň dvojnásobek obvyklé doby — zavolat mu, nebo vyřešit s důvodem',
  // Migrace 4009 (11. 10. 2026, W50) — kdo platí: neplatný plátce firmy, ruční odběratel zakázky bez důvodu.
  platce_neplatny:
    'Firma platí přes jinou firmu, které faktura nedojde nebo nemá smlouvu — opravit „Kdo platí“ na kartě firmy',
  odberatel_bez_duvodu:
    'Zakázka se ručně fakturuje jiné firmě bez důvodu — doplnit důvod, nebo vrátit podle karty firmy',
  // Migrace 4202 (12. 10. 2026, W61) — odvoz ze skladu bez přijaté faktury déle než N dní.
  odvoz_bez_faktury:
    'Odvozy ze skladu čekají na fakturu koncovky — ozvat se koncovce a fakturu navázat na odvoz',
  // Migrace 4240 (12. 10. 2026, W63) — nájem nádob ve smlouvě nesedí s obalovým kontem.
  najem_nadob_nesoulad:
    'Počet nádob ve smlouvě nesedí s obalovým kontem — srovnat výdej a vrácení obalů, nebo opravit smlouvu',
  // Migrace 4262 (12. 10. 2026, W64) — mostní váze končí metrologické ověření.
  vaha_overeni_konci:
    'Mostní váze končí metrologické ověření — objednat ověření a zapsat nové datum v Nastavení váhy',
  // Migrace 4282 (12. 10. 2026, W65) — přeplatek zálohy čeká na rozhodnutí.
  preplatek_ceka_na_rozhodnuti:
    'Přeplatek zálohy čeká na rozhodnutí — vrátit zákazníkovi, nebo převést jako kredit na příští fakturu',
  // Migrace 4310 (12. 10. 2026, W66) — vozidlo mimo provoz má naplánované zakázky.
  vozidlo_mimo_provoz_zakazka:
    'Vozidlo je mimo provoz, ale má naplánované zakázky — přidělit jim jiné vozidlo, nebo vozidlo vrátit do provozu',
  // Migrace 4520 (16. 10. 2026, W70) — dnes je plánovaná odstávka CRŽP a ve frontě něco čeká.
  crzp_odstavka_dnes:
    'Dnes je plánovaná odstávka CRŽP — co čeká ve frontě, odejde samo po jejím konci',
  // Migrace 4641 (18. 10. 2026, 05/2971) — od 20. dne měsíce zkontrolovat smluvní cenu v EUR u zákazníka.
  smluvni_cena_eur_kontrola:
    'Zákazník má cenu v EUR, která se mění podle trhu — zkontrolovat oznámení o nové ceně a zapsat ji na kartě zákazníka',
  // Migrace 4820 (20. 10. 2026, W72) — účet nebo veřejný odkaz vyčerpal denní limit volání AI (05/3740).
  ai_limit_vycerpan:
    'Účet nebo odkaz na portál podkladů dnes vyčerpal denní limit volání AI — ověřit, co se děje; limit je v Nastavení',
  nadoby_po_konci_smlouvy: 'Nádoby po konci smlouvy nejsou zpět',
  dodatek_bez_podpisu: 'Dodatek čeká na podpis',
  // Migrace 5100 (22. 10. 2026) — doklad od odběratele čeká na spárování se zakázkou a fakturu.
  podklad_prodeje_ceka:
    'Objednávka nebo vážní lístek od odběratele čeká na fakturu — spárovat se zakázkou a vystavit fakturu do 15 dní od plnění',
  // Migrace 5110 (22. 10. 2026) — vývoz zboží bez doloženého rozhodnutí celního úřadu.
  vyvoz_nedolozen:
    'Faktura s vývozem mimo EU nemá doložený vývoz — nahrát rozhodnutí celního úřadu do 30 dní od plnění, jinak dodanit',
  // Migrace 5161 (24. 10. 2026, W80) — firmy se sjednanými audity bez auditora.
  audit_firma_bez_auditora:
    'Firmy se sjednanými audity nemají auditora — přidělit je v Audity › Firmy auditorů',
  // Migrace 5162 (W80 díl 3) — lhůta auditu podle typu auditu.
  audit_typ_lhuta:
    'Firmám se sjednanými audity uplynula lhůta od posledního auditu a další není v plánu — naplánovat audit v sekci Audity',
  // Migrace 5261 (26. 10. 2026, W81) — hotové zakázky čekají na fakturu, blíží se lhůta pro daňový doklad.
  zakazka_lhuta_dokladu:
    'Hotové zakázky čekají na fakturu a blíží se lhůta pro daňový doklad — vystavit fakturu do 15 dní od plnění (souhrnnou do 15. dne dalšího měsíce)',
  // Migrace 5290 (26. 10. 2026) — záloha databáze mimo server.
  zaloha_db_mimo_server:
    'Záloha databáze mimo server neproběhla déle než 2 dny — zkontrolovat připojení Microsoft 365 v Nastavení › Zálohování',
  // Migrace 5300 (26. 10. 2026) — časová razítka RFC 3161 na podepsané dokumenty.
  casove_razitko_nejde:
    'Podepsaný dokument čeká na časové razítko déle než den — zkontrolovat autoritu v Nastavení › Automatika a propojení',
  casove_razitko_balicek:
    'V předplaceném balíčku časových razítek dochází zásoba — dobít balíček u autority',
  // Migrace 5400 (27. 10. 2026) — odpověď zákazníka na nabídku z pošty.
  nabidka_odpoved_ceka:
    'Zákazník odpověděl na nabídku e‑mailem a odpověď čeká na obchodníka — potvrdit přijetí, označit odmítnutou, nebo odpovědět na dotaz',
  // Migrace 5600 (W82 díl 1, 30. 10. 2026) — vozidlo na operativní leasing.
  vozidlo_leasing:
    'Vozidlo na leasing — blíží se konec smlouvy a podmínky vrácení (STK, servis), nájezd míří nad sjednaný limit, za měsíc chybí splátka, nebo je faktura leasingu po splatnosti',
  // Migrace 5610 (W83 díl 2b, 30. 10. 2026, 05/4311 c) — adresy zákazníků bez IČZÚJ.
  zakaznik_adresa_bez_zuj:
    'Zákazníci mají adresu, ale bez IČZÚJ — doplnit z RÚIAN v Zákaznících › Adresy bez IČZÚJ (nejdřív náhled)',
  // Migrace 5800 (W92 PR 2, 30. 10. 2026) — faktura za servis vozidla bez servisního záznamu.
  vozidlo_servis_bez_zaznamu:
    'Schválená faktura za servis vozidla nemá servisní záznam — zapsat servis z faktury, nebo označit, že to není servis',
  // Migrace 5891 (C4/C7 kniha jízd soukromého užívání) — neodevzdaná kniha, soukromé km u limitu, nulový nájezd.
  kniha_jizd:
    'Kniha jízd služebního vozidla se soukromým užíváním — neodevzdaná do 3. pracovního dne, soukromé km u limitu nebo nad ním, nebo nulový nájezd se spotřebou paliva',
  // Migrace 6000 (rozhodnutí zadavatele 31. 10. 2026) — připomínka výpisu z MOL za minulý měsíc.
  tankovani_vypis_mesic:
    'Výpis z MOL za minulý měsíc není nahraný — stáhnout export z Online Card Center a nahrát ho ve Vozidla › Tankování (MOL)',
  // Migrace 6250 (D8a Vymáhání, 28. 10. 2026) — splátky a lhůty případů vymáhání, jednateli a adminovi.
  vymahani_splatka_chybi:
    'Vymáhání: splátka podle splátkového plánu případu nepřišla — dlužník je pozadu',
  vymahani_lhuta: 'Vymáhání: lhůta případu (soud, insolvence, advokát) se blíží nebo uplynula',
  // Migrace 6260 (D8b Balíček pro advokáta, 28. 10. 2026) — po lhůtě 2. upomínky, jednateli a adminovi.
  pohledavka_k_advokatovi:
    'Lhůta z 2. upomínky uplynula a faktury jsou nezaplacené — nabídka předat je advokátovi',
  // Migrace 6280 (D10) — pokuta z přijaté pošty čeká na vyřízení.
  pokuta_k_vyreseni:
    'Pokuta z přijaté pošty čeká na vyřízení — doplnit řidiče, zaplatit do lhůty, po zaplacení založit srážku ze mzdy nebo prominout, nebo sdělit řidiče úřadu',
  // Migrace 6295 (D13, 1. 11. 2026) — stav peněz a výhled peněz.
  penize_odsouhlasit_mesic:
    'Stav peněz za minulý měsíc není odsouhlasený — v Účetnictví › Stav peněz opravit, co nesedí (banka proti výpisu, nespárované platby, nepotvrzené faktury, export pro účetní), a odsouhlasit',
  penize_nedostatek_na_davku:
    'Na platební dávku podle výhledu peněz na 30 dní k jejímu datu nebudou peníze ani s očekávanými příjmy — posunout méně naléhavé platby, nebo zajistit příjem',
  // Migrace 6340 (W82 díl 4) — lhůty nájmu vozidla na leasing.
  vozidlo_najem:
    'Lhůta nájmu vozidla na leasing — končí platnost nabídky, objednávka čeká na potvrzení, blíží se termín vrácení, chybí závěrečné vyúčtování, nebo je čas vypovědět rámcovou smlouvu',
  // Migrace 6341 (W82, 05/4456 a) — výzva k úhradě ze soukromého užívání vozidla po splatnosti.
  vyzva_uhrady_po_splatnosti:
    'Zaměstnanec nezaplatil po splatnosti výzvu k úhradě nesražené části vyúčtování soukromého užívání vozidla',
  // Migrace 6400 (D5) — e‑shop zaplacený bez faktury, účtenka kartou bez nákupu.
  platba_ceka_na_fakturu:
    'Zaplaceno předem (e‑shop, karta nebo převod) a faktura pořád nepřišla — vyžádat fakturu od prodejce, nebo ji připojit ručně',
  uctenka_karta_bez_nakupu:
    'Účtenka placená kartou nemá nákup na výpisu firemních karet — platil zaměstnanec svou kartou? Pak proplatit',
  // Migrace 6430 (D15) — samofaktura od odběratele čeká na zaevidování (tržba a DPH).
  samofaktura_k_zaevidovani:
    'Samofaktura od odběratele čeká na zaevidování — bez něj chybí tržba a DPH za měsíc plnění (přiznání do 25. dne dalšího měsíce)',
  // Migrace 6440 (D16) — výplaty Comgate: rozdíly k vyřešení.
  comgate_rozdil:
    'Výplaty Comgate: výplata nesedí na platby kartou nebo v bance chybí, výplata bez rozpisu, platba kartou bez záznamu v aplikaci nebo vrácení platby — k vyřešení v Bance › Výplaty Comgate',
  // Migrace 6920 (05/4984 b) — doklad k přijaté platbě zálohy sám nevznikl.
  doklad_k_platbe_nevznikl:
    'Daňový doklad k přijaté platbě zálohy po platbě sám nevznikl — vystavit ho do 15 dní od přijetí platby (§ 28 odst. 2 zákona o DPH)',
  // Migrace 7020 (W114) — dokončená zakázka bez řádku průběžné evidence.
  evidence_zakazka_bez_radku:
    'Dokončená zakázka s odpadem na našem zařízení nemá řádek průběžné evidence — chybí pohyb skladu, řádek z pohybu, předání partnerovi nebo hmotnost',
};

/** Signály, které se vyřeší jen s napsaným důvodem (server bez něj vrátí 400). */
export const DRUHY_S_DUVODEM: ReadonlySet<string> = new Set([
  'zakaznik_prestal_odebirat',
  'tankovani_vypis_mesic',
]);

/** Popis druhu: přednost má `druh_popis` ze serveru, jinak záloha podle klíče, jinak klíč. */
export function popisDruhu(s: Pick<Signal, 'druh' | 'druh_popis'>): string {
  return s.druh_popis || DRUHY_SIGNALU[s.druh as DruhSignalu] || s.druh;
}

/** Skloňování „den / dny / dní“. */
function dni(n: number): string {
  if (n === 1) return '1 den';
  if (n >= 2 && n <= 4) return `${n} dny`;
  return `${n} dní`;
}

/**
 * Termín slovy z `za_dni`: „za 12 dní“, „za 3 dny“, „zítra“, „dnes“, „po termínu 1 den“, „po termínu 3 dny“;
 * bez údaje prázdný řetězec.
 */
export function popisTerminu(zaDni: number | undefined | null): string {
  if (zaDni === undefined || zaDni === null || !Number.isFinite(zaDni)) return '';
  if (zaDni === 0) return 'dnes';
  if (zaDni === 1) return 'zítra';
  if (zaDni > 0) return `za ${dni(zaDni)}`;
  return `po termínu ${dni(-zaDni)}`;
}

/** Signály seřazené závažnost → termín a oříznuté na `SIGNALU_NA_DOMU` (server řadí stejně; pojistka pro UI). */
export function nejzavaznejsi(signaly: readonly Signal[], nejvyse = SIGNALU_NA_DOMU): Signal[] {
  return [...signaly]
    .sort(
      (a, b) =>
        PORADI_ZAVAZNOSTI[a.zavaznost] - PORADI_ZAVAZNOSTI[b.zavaznost] ||
        (a.za_dni ?? Number.MAX_SAFE_INTEGER) - (b.za_dni ?? Number.MAX_SAFE_INTEGER),
    )
    .slice(0, nejvyse);
}
